import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';
import { cn } from '../cn';

type SheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  side?: 'left' | 'right' | 'bottom';
  /** `navy` renders the panel on the brand navy surface (e.g. mobile navigation). */
  tone?: 'light' | 'navy';
  className?: string;
  /** Element to focus when the sheet closes (normally the control that opened it). */
  returnFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
};

const sideClasses = {
  left: 'inset-y-0 left-0 h-full w-[min(20rem,85vw)]',
  right: 'inset-y-0 right-0 h-full w-[min(28rem,100vw)]',
  bottom: 'inset-x-0 bottom-0 max-h-[85vh] w-full rounded-t-lg',
} as const;

/** Modal side panel (Radix Dialog): focus trapped, Escape closes, focus returns to trigger. */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  side = 'right',
  tone = 'light',
  className,
  returnFocusRef,
  children,
}: SheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-navy-950/50" />
        <Dialog.Content
          onCloseAutoFocus={(event) => {
            if (returnFocusRef?.current) {
              event.preventDefault();
              returnFocusRef.current.focus();
            }
          }}
          className={cn(
            'fixed z-50 flex flex-col overflow-y-auto shadow-lg focus:outline-none',
            tone === 'navy' ? 'on-navy bg-navy-950' : 'bg-white',
            sideClasses[side],
            className,
          )}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div
            className={cn(
              'flex items-start justify-between gap-4 border-b p-4',
              tone === 'navy' ? 'border-navy-800' : 'border-line-200',
            )}
          >
            <div>
              <Dialog.Title
                className={cn(
                  'font-serif text-lg font-semibold',
                  tone === 'navy' ? 'text-white' : 'text-navy-900',
                )}
              >
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description className="text-sm text-muted-600">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close
              className={cn(
                'rounded-md p-2',
                tone === 'navy'
                  ? 'text-white hover:bg-navy-800'
                  : 'text-navy-900 hover:bg-navy-100',
              )}
              aria-label="Close panel"
            >
              <X className="size-4" aria-hidden />
            </Dialog.Close>
          </div>
          <div className="flex-1 p-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
